import { Eye } from 'lucide-react';
import type { Profile } from '@/types';
import { useAuth } from '@/app/providers/AuthProvider';
import { getEmblem, getTitle } from '../cosmetics';
import { parseLinks } from '../helpers';
import { useLocker } from '../lockerStore';
import { useUpdateProfileLinks, useUpdateProfileVisibility } from '../hooks/useProfile';
import type { VisibilityPatch } from '../api/profiles';
import { ProfilePreviewCard } from './ProfilePreviewCard';
import { SocialLinks } from './SocialLinks';
import { ProfileMeta } from './ProfileMeta';
import { ProfileEditor } from './ProfileEditor';
import { AboutSection } from './AboutSection';
import { LockerSignpost } from './LockerSignpost';

/**
 * The full-screen self-profile workspace (`/me`). Three panels:
 *
 *  - **Left** — the Locker signpost: equip themes, titles, and emblems. The
 *    Locker is the single global cosmetics surface (see `lockerStore`), so this
 *    panel just shows what's equipped and opens it.
 *  - **Middle** — your editable profile info: hero preview, social links, and the
 *    public-profile editor. No Title Shop here — that lives inside the Locker.
 *  - **Right** — a live reflection of how you appear to others, mirroring what
 *    members see when they tap your name inside a community (read-only).
 */
export function ProfileWorkspace({ profile }: { profile: Profile }) {
  const openLocker = useLocker((s) => s.openLocker);
  const { refreshProfile } = useAuth();
  const updateVisibility = useUpdateProfileVisibility();
  const updateLinks = useUpdateProfileLinks();
  const title = getTitle(profile.title_id);
  const emblem = getEmblem(profile.emblem_id);
  const links = parseLinks(profile.links);

  const saveVisibility = (patch: VisibilityPatch) =>
    updateVisibility.mutate({ id: profile.id, patch }, { onSuccess: () => void refreshProfile() });

  const saveLinks = (next: Record<string, string>) =>
    updateLinks.mutate({ id: profile.id, links: next }, { onSuccess: () => void refreshProfile() });
  const addLink = (platform: string, url: string) => saveLinks({ ...links, [platform]: url });
  const removeLink = (platform: string) => {
    const next = { ...links };
    delete next[platform];
    saveLinks(next);
  };

  return (
    // Two columns, no sticky panels — the whole page scrolls as one (the editable
    // profile and the public-reflection column scroll together).
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,420px)]">
      {/* MAIN — editable profile info (no Title Shop). The About controls live in
          the editor card's right column (its aside), not as a separate panel. */}
      <section>
        <h1 className="mb-5 text-sm font-extrabold uppercase tracking-[2px] text-accent">
          Profile
        </h1>
        <ProfileEditor
          profile={profile}
          links={links}
          onAddLink={addLink}
          onRemoveLink={removeLink}
          aside={
            <AboutSection
              embedded
              userId={profile.id}
              editable
              showFriends={profile.show_friends}
              showCommunities={profile.show_communities}
              onToggleFriends={(v) => saveVisibility({ show_friends: v })}
              onToggleCommunities={(v) => saveVisibility({ show_communities: v })}
            />
          }
          asideFooter={<LockerSignpost title={title} emblem={emblem} onOpen={openLocker} />}
        />
      </section>

      {/* RIGHT — public reflection, with the Locker beneath it. */}
      <aside>
        <div className="mb-3 flex items-center gap-2.5">
          <span className="grid h-8 w-8 flex-none place-items-center rounded-full bg-white/[0.06] text-mid">
            <Eye size={15} strokeWidth={2} />
          </span>
          <div>
            <div className="text-sm font-extrabold text-hi">How others see you</div>
            <div className="text-[11px] text-mid">What members see in a community.</div>
          </div>
        </div>
        <section className="rounded-2xl border border-edge bg-white/[0.03] p-4">
          <ProfilePreviewCard profile={profile} title={title} emblem={emblem} />
          <SocialLinks links={links} />
          <ProfileMeta profile={profile} />
          <AboutSection
            userId={profile.id}
            showFriends={profile.show_friends}
            showCommunities={profile.show_communities}
          />
        </section>
      </aside>
    </div>
  );
}
